import { useEffect, useRef } from 'react';
import { useConsent } from '../context/ConsentContext';
import { useApp } from '../context/AppContext';

// Platform-wide tracking configuration
// In a real app, this would come from a central settings API
const PLATFORM_CURRENCY = 'EUR';

const PLATFORM_CONFIG = {
  // Platform's own tracking (optional)
  platformGtmId: '', // e.g., 'GTM-PLATFORM123'
};

// Store consent globally for tracking functions
let globalConsent = { analytics: false, marketing: false, preferences: false };

export default function TrackingScripts() {
  const { consent, isInitialized } = useConsent();
  const { currentOrganizer } = useApp();
  const scriptsLoadedRef = useRef({
    gtm: false,
    meta: false,
    googleAds: false,
  });

  // Get settings from current organizer context
  const organizerSettings = currentOrganizer?.settings || {};
  const {
    metaPixelId = '',
    googleAdsId = '',
    googleTagManagerId = '',
  } = organizerSettings;

  // Update global consent for tracking functions
  useEffect(() => {
    globalConsent = { ...consent };
    window.__tikt_consent = consent;
  }, [consent]);

  // Load Google Tag Manager
  useEffect(() => {
    if (!isInitialized) return;

    const gtmId = googleTagManagerId || PLATFORM_CONFIG.platformGtmId;
    if (!gtmId || scriptsLoadedRef.current.gtm) return;

    // GTM loads regardless of consent, but respects consent mode
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

    // Initialize dataLayer if not already done
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    document.head.appendChild(script);
    scriptsLoadedRef.current.gtm = true;

    // Also add noscript iframe for GTM
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      // Cleanup is complex with GTM, typically not done
    };
  }, [googleTagManagerId, isInitialized]);

  // Load Meta Pixel (only after marketing consent)
  useEffect(() => {
    if (!isInitialized || !consent.marketing) return;
    if (!metaPixelId || scriptsLoadedRef.current.meta) return;

    // Meta Pixel initialization
    !function(f,b,e,v,n,t,s) {
      if(f.fbq)return;
      n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];
      t=b.createElement(e);t.async=!0;
      t.src=v;
      s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s);
    }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', metaPixelId);
    window.fbq('track', 'PageView');
    scriptsLoadedRef.current.meta = true;
  }, [metaPixelId, consent.marketing, isInitialized]);

  // Load Google Ads (only after marketing consent)
  useEffect(() => {
    if (!isInitialized || !consent.marketing) return;
    if (!googleAdsId || scriptsLoadedRef.current.googleAds) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`;
    document.head.appendChild(script);

    script.onload = () => {
      window.gtag('config', googleAdsId);
    };

    scriptsLoadedRef.current.googleAds = true;
  }, [googleAdsId, consent.marketing, isInitialized]);

  // Track consent changes
  useEffect(() => {
    if (!isInitialized) return;

    // Push consent update to dataLayer for GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'consent_update',
      consent_analytics: consent.analytics,
      consent_marketing: consent.marketing,
      consent_preferences: consent.preferences,
    });
  }, [consent, isInitialized]);

  return null; // This component doesn't render anything
}

// Event types and their required consent levels
const EVENT_CONSENT_REQUIREMENTS = {
  // Essential events (always tracked)
  'page_view': 'necessary',
  'purchase': 'necessary',
  'begin_checkout': 'necessary',
  // Analytics events
  'view_event': 'analytics',
  'add_to_cart': 'analytics',
  'select_seat': 'analytics',
  // Marketing events
  'lead': 'marketing',
  'ad_click': 'marketing',
};

// Track specific events (respects consent)
export function trackEvent(eventName, eventParams = {}) {
  const consent = window.__tikt_consent || globalConsent;
  const requiredConsent = EVENT_CONSENT_REQUIREMENTS[eventName] || 'analytics';

  // Check if we have the required consent level
  if (requiredConsent !== 'necessary' && !consent[requiredConsent]) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Tracking] Skipped ${eventName} - no ${requiredConsent} consent`);
    }
    return;
  }

  // Push to dataLayer for GTM
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventParams,
  });

  // Track with Meta Pixel if available and marketing consent given
  if (consent.marketing && typeof window.fbq === 'function') {
    window.fbq('trackCustom', eventName, eventParams);
  }

  // Track with gtag if available and appropriate consent given
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
}

// Pre-defined event helpers
export const trackingEvents = {
  pageView: (pagePath) => {
    trackEvent('page_view', { page_path: pagePath });
  },

  viewEvent: (eventId, eventTitle) => {
    trackEvent('view_event', {
      event_id: eventId,
      event_title: eventTitle,
    });
  },

  addToCart: (eventId, ticketType, price, quantity) => {
    trackEvent('add_to_cart', {
      event_id: eventId,
      ticket_type: ticketType,
      price: price,
      quantity: quantity,
      value: price * quantity,
      currency: PLATFORM_CURRENCY,
    });
  },

  beginCheckout: (eventId, totalValue) => {
    trackEvent('begin_checkout', {
      event_id: eventId,
      value: totalValue,
      currency: PLATFORM_CURRENCY,
    });
  },

  purchase: (orderId, eventId, totalValue) => {
    trackEvent('purchase', {
      transaction_id: orderId,
      event_id: eventId,
      value: totalValue,
      currency: PLATFORM_CURRENCY,
    });
  },

  selectSeat: (eventId, sectionName, seatId) => {
    trackEvent('select_seat', {
      event_id: eventId,
      section: sectionName,
      seat_id: seatId,
    });
  },
};
